/**
 * The workflow chart's rules, without React: what steps there are and how a
 * run's step status reads on the chart (tone, label, the failure bubble).
 *
 * The graph rules themselves (what can run) are `@repo/shared/workflowGraph`,
 * shared with the server, so the chart warns about exactly what Run refuses.
 *
 * @module components/workflows/workflowCanvas.logic
 */
import {
  WORKFLOW_LIMITS,
  type WorkflowGraph,
  type WorkflowGraphIssue,
  type WorkflowIngestionField,
  type WorkflowIntakePick,
  type WorkflowIntakeSource,
  type WorkflowIntakeSourceKind,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowPromptEffort,
  type WorkflowPromptModel,
  type WorkflowRun,
  type WorkflowScheduleRule,
  type WorkflowScheduleView,
  type WorkflowStepField,
  type WorkflowStepRun,
  type WorkflowStepStatus,
} from "@amoreg/contracts";

import {
  MAIL_DAYS_DEFAULT,
  MAIL_DAYS_MAX,
  workflowAncestors,
  workflowApproverEmails,
  workflowChecklistPeople,
  workflowTopologicalOrder,
} from "@repo/shared/workflowGraph";
import { WORKFLOW_STEP_LABEL } from "@repo/shared/workflowEdit";

import type { AutomationSchedule } from "../../state/automations";

/** ⌘Z, ⌘⇧Z or ⌘Y (Ctrl on other systems): the chart's undo and redo. */
export const isUndoChord = (event: { readonly key: string; readonly metaKey: boolean; readonly ctrlKey: boolean }) =>
  (event.metaKey || event.ctrlKey) && (event.key.toLowerCase() === "z" || event.key.toLowerCase() === "y");

export interface StepKind {
  readonly type: WorkflowNodeType;
  readonly label: string;
  readonly description: string;
}

/** The steps the editor offers, in the order it lists them. */
export const STEP_KINDS: ReadonlyArray<StepKind> = [
  { type: "prompt", label: WORKFLOW_STEP_LABEL.prompt, description: "Ask the AI to do something with the input and earlier steps." },
  { type: "condition", label: WORKFLOW_STEP_LABEL.condition, description: "Go one way on Yes and another on No." },
  { type: "for-each", label: WORKFLOW_STEP_LABEL["for-each"], description: "Do the same for every row of a table, and gather the answers into one." },
  { type: "compare", label: WORKFLOW_STEP_LABEL.compare, description: "Check two tables row by row by fixed rules; Yes when something breaks one." },
  {
    type: "browser-task",
    label: WORKFLOW_STEP_LABEL["browser-task"],
    description: "Run a saved browser task, such as downloading this week's notices from a site.",
  },
  { type: "intake", label: WORKFLOW_STEP_LABEL.intake, description: "Gather documents from the library, a space, SharePoint or Google Drive." },
  { type: "ingestion", label: WORKFLOW_STEP_LABEL.ingestion, description: "Read the input into a fixed set of fields, and fill a template." },
  { type: "approval", label: WORKFLOW_STEP_LABEL.approval, description: "Wait for a person to approve, reject or send it back." },
  { type: "checklist", label: WORKFLOW_STEP_LABEL.checklist, description: "Wait for a person to do something outside Amoreg and tick it done." },
  { type: "chat", label: WORKFLOW_STEP_LABEL.chat, description: "Open a chat with the results, to follow up on them." },
  { type: "notify", label: WORKFLOW_STEP_LABEL.notify, description: "Send members a notice in their Inbox, linking to the run." },
  { type: "save-file", label: WORKFLOW_STEP_LABEL["save-file"], description: "Save the results as a new document or Excel workbook in a space." },
  { type: "email", label: WORKFLOW_STEP_LABEL.email, description: "Email the results to members of this organization." },
  { type: "output", label: WORKFLOW_STEP_LABEL.output, description: "Show what reaches it on the run page. Optional: any step can end a workflow." },
  { type: "start", label: WORKFLOW_STEP_LABEL.start, description: "Where a run begins, with what you type when you press Run." },
];

export const MODEL_CHOICES: ReadonlyArray<{ readonly value: WorkflowPromptModel; readonly label: string }> = [
  { value: "standard", label: "Amoreg" },
  { value: "pro", label: "Amoreg Pro" },
];

export const EFFORT_CHOICES: ReadonlyArray<{ readonly value: WorkflowPromptEffort; readonly label: string }> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "xhigh", label: "High" },
];

/**
 * An Ingestion step's fields with suggestions added: the ones already listed
 * (blank rows dropped) stay as they are, a suggestion whose name is already
 * there (ignoring case and spaces) is left out, and the list stops at the
 * step's limit. `added` counts the suggestions that made it in.
 */
export function mergeSuggestedFields(
  current: ReadonlyArray<WorkflowIngestionField>,
  suggested: ReadonlyArray<WorkflowIngestionField>,
): { readonly fields: ReadonlyArray<WorkflowIngestionField>; readonly added: number } {
  const fold = (name: string) => name.trim().toLowerCase().replace(/\s+/g, " ");
  const kept = current.filter((field) => field.name.trim() !== "" || field.description.trim() !== "");
  const named = new Set(kept.map((field) => fold(field.name)));
  const added = suggested.filter((field) => {
    const key = fold(field.name);
    if (!key || named.has(key)) return false;
    named.add(key);
    return true;
  });
  const fields = [...kept, ...added].slice(0, WORKFLOW_LIMITS.ingestionFields);
  return { fields, added: fields.length - kept.length };
}

/** The fields an Ingestion step could not find on a run, by name. */
export const missingFieldNames = (fields: ReadonlyArray<WorkflowStepField>): ReadonlyArray<string> =>
  fields.filter((field) => field.value === null).map((field) => field.name);

/** What each Intake source is called on the chart and in the panel. */
export const INTAKE_SOURCE_LABEL: Readonly<Record<WorkflowIntakeSourceKind, string>> = {
  library: "the library",
  space: "Space files",
  sharepoint: "SharePoint",
  googledrive: "Google Drive",
  email: "email",
};

/** Mail an email source reads by default, and at most: the last this many days. */
export const MAIL_DAYS = { default: MAIL_DAYS_DEFAULT, max: MAIL_DAYS_MAX } as const;

/**
 * A new Intake source of `kind`: nothing picked, no search; a space source
 * starts in the workflow's space, an email source in Gmail with the last 30
 * days and nothing yet to narrow it.
 */
export const newIntakeSource = (kind: WorkflowIntakeSourceKind, spaceId: string | null): WorkflowIntakeSource => ({
  kind,
  location: kind === "space" ? (spaceId ?? "") : kind === "email" ? "gmail" : "",
  picks: [],
  query: "",
  ...(kind === "email" ? { mail: { from: "", subject: "", label: "", days: MAIL_DAYS_DEFAULT } } : {}),
});

/** A source with one more pick, unless it is already there or the source is full. */
export function addIntakePick(source: WorkflowIntakeSource, pick: WorkflowIntakePick): WorkflowIntakeSource {
  if (source.picks.some((entry) => entry.id === pick.id) || source.picks.length >= WORKFLOW_LIMITS.intakePicks) return source;
  return { ...source, picks: [...source.picks, pick] };
}

/* ------------------------------------------------------------------ */
/* Run status on the chart                                             */
/* ------------------------------------------------------------------ */

export type StepTone = "idle" | "running" | "done" | "waiting" | "failed" | "skipped";

export function stepTone(status: WorkflowStepStatus | null): StepTone {
  switch (status) {
    case "running":
      return "running";
    case "succeeded":
      return "done";
    case "waiting":
      return "waiting";
    case "failed":
      return "failed";
    case "skipped":
      return "skipped";
    default:
      return "idle";
  }
}

export const STEP_STATUS_LABEL: Readonly<Record<WorkflowStepStatus, string>> = {
  pending: "Not started",
  running: "Running",
  waiting: "Waiting for approval",
  succeeded: "Done",
  failed: "Failed",
  skipped: "Skipped",
  cancelled: "Stopped",
};

/** A step's status as its panel and node say it: an Intake step waits for a mailbox permission, a manual step to be done. */
export const stepStatusLabel = (status: WorkflowStepStatus, type: WorkflowNodeType): string =>
  status === "waiting" && type === "intake"
    ? "Waiting for permission"
    : status === "waiting" && type === "checklist"
      ? "Waiting to be done"
      : STEP_STATUS_LABEL[status];

export const RUN_STATUS_LABEL: Readonly<Record<WorkflowRun["status"], string>> = {
  queued: "Starting",
  running: "Running",
  waiting: "Waiting",
  succeeded: "Finished",
  failed: "Failed",
  cancelled: "Stopped",
};

/**
 * Whether this person may retry a run's steps: only the person who started
 * it (its steps act, and are billed, as them; the server refuses anyone else).
 */
export function canRetryRun(
  run: Pick<WorkflowRun, "startedByEmail"> | null,
  viewerEmail: string | null | undefined,
): boolean {
  if (!run?.startedByEmail || !viewerEmail) return false;
  return run.startedByEmail.trim().toLowerCase() === viewerEmail.trim().toLowerCase();
}

/** Each step's run state by step id; empty when no run is shown. */
export function stepsByNode(run: WorkflowRun | null): ReadonlyMap<string, WorkflowStepRun> {
  return new Map((run?.steps ?? []).map((step) => [step.nodeId, step]));
}

/** Each step's problems by step id, for the warning marker on its card. */
export function issuesByNode(issues: ReadonlyArray<WorkflowGraphIssue>): ReadonlyMap<string, ReadonlyArray<WorkflowGraphIssue>> {
  const byNode = new Map<string, WorkflowGraphIssue[]>();
  for (const issue of issues) {
    if (!issue.nodeId) continue;
    byNode.set(issue.nodeId, [...(byNode.get(issue.nodeId) ?? []), issue]);
  }
  return byNode;
}

/** "12s", "3m 05s", "1h 02m" for a step that has started; null otherwise. */
export function stepDuration(step: Pick<WorkflowStepRun, "startedAt" | "finishedAt">, now: number): string | null {
  const started = step.startedAt ? Date.parse(step.startedAt) : Number.NaN;
  if (!Number.isFinite(started)) return null;
  const finished = step.finishedAt ? Date.parse(step.finishedAt) : now;
  const seconds = Math.max(0, Math.round((finished - started) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
  // A step that waited days (an approval, a manual step) reads in days, not "364h 37m" (AMO-306).
  const days = Math.floor(hours / 24);
  return days === 1 ? `1 day ${hours % 24}h` : `${days} days`;
}

/**
 * The one line a step's card shows under its name while editing. A Browser
 * task step names its task: from the run's frozen copy when there is one,
 * else from `taskNames` (the viewer's tasks by id).
 */
export function stepSubtitle(node: WorkflowNode, taskNames?: ReadonlyMap<string, string>): string {
  switch (node.type) {
    case "start":
      return node.config.inputHint?.trim() || "What you type when you press Run";
    case "output":
      return "The run's result";
    case "prompt": {
      const text = node.config.instructions.trim().replace(/\s+/g, " ");
      return text ? (text.length > 80 ? `${text.slice(0, 79)}…` : text) : "No instructions yet";
    }
    case "browser-task": {
      const taskId = node.config.taskId.trim();
      if (!taskId) return "Choose a browser task";
      const name = node.config.task?.definition.name ?? taskNames?.get(taskId);
      // Unknown with the list loaded: deleted, or someone else's personal task.
      return name ?? (taskNames ? "A browser task you cannot use" : "Browser task");
    }
    case "condition": {
      if (node.config.mode === "contains") {
        const text = node.config.text.trim();
        return text ? `Yes if the result contains "${text.length > 40 ? `${text.slice(0, 39)}…` : text}"` : "No text to look for yet";
      }
      const question = node.config.question.trim().replace(/\s+/g, " ");
      return question ? (question.length > 80 ? `${question.slice(0, 79)}…` : question) : "No question yet";
    }
    case "for-each": {
      if (!node.config.source.step) return "Choose the table to go through";
      return node.config.instructions.trim() ? `Each row: ${node.config.instructions.trim().slice(0, 60)}` : "Say what to do for each row";
    }
    case "compare": {
      const { key, rules } = node.config;
      if (!node.config.left.step || !node.config.right.step) return "Choose the two tables to compare";
      if (!key.trim()) return "Name the column rows are matched on";
      const checks = rules.length === 0 ? "rows only one table has" : rules.length === 1 ? "1 rule" : `${rules.length} rules`;
      return `Matched on ${key.trim()}, checking ${checks}`;
    }
    case "checklist": {
      if (!node.config.instructions.trim()) return "Say what has to be done";
      const people = node.config.assigneeEmails;
      if (people.length === 0) return "Done by the person who runs it";
      return people.length === 1 ? `Done by ${people[0]}` : `Done by ${people[0]} or ${people.length - 1} more`;
    }
    case "approval": {
      const approvers = node.config.approverEmails;
      if (approvers.length === 0) return "Choose who approves";
      return approvers.length === 1 ? `Approver: ${approvers[0]}` : `Approvers: ${approvers[0]} and ${approvers.length - 1} more`;
    }
    case "intake": {
      const names = [...new Set(node.config.sources.map((source) => INTAKE_SOURCE_LABEL[source.kind]))];
      if (names.length === 0) return "No sources yet";
      return names.length <= 2 ? `Reads ${names.join(" and ")}` : `Reads ${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
    }
    case "chat":
      return node.config.title.trim() ? `Chat: ${node.config.title.trim()}` : "A chat with the results, for the run's person";
    case "notify":
    case "email": {
      const people = node.config.recipientEmails;
      const verb = node.type === "email" ? "Email" : "Notify";
      if (people.length === 0) return node.type === "email" ? "Choose who gets the email" : "Choose who gets the notice";
      return people.length === 1 ? `${verb} ${people[0]}` : `${verb} ${people[0]} and ${people.length - 1} more`;
    }
    case "save-file":
      if (!node.config.spaceId) return "Choose the space to save into";
      return node.config.format === "excel"
        ? "A new Excel workbook in the space, each run"
        : node.config.format === "word"
          ? "A new Word document in the space, each run"
          : "A new document in the space, each run";
    case "ingestion": {
      const names = node.config.fields.map((field) => field.name.trim()).filter(Boolean);
      if (names.length === 0) return "No fields yet";
      const list = names.length <= 3 ? names.join(", ") : `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
      return list.length > 80 ? `${list.slice(0, 79)}…` : list;
    }
  }
}


/** The optional fields a schedule rule may carry, besides frequency, time and zone. */
const SCHEDULE_FIELDS = ["date", "everyMinutes", "until", "weekday", "dayOfMonth", "startDate", "endDate"] as const;

/** The schedule picker's starting point: the saved rule, or weekdays at 9:00 in the person's zone. */
export function scheduleDraftOf(schedule: WorkflowScheduleView | null | undefined, timezone: string): AutomationSchedule {
  if (!schedule) return { frequency: "weekdays", time: "09:00", timezone };
  const { rule } = schedule;
  const draft: Record<string, unknown> = { frequency: rule.frequency, time: rule.time, timezone: rule.timezone };
  for (const field of SCHEDULE_FIELDS) if (rule[field] !== undefined) draft[field] = rule[field];
  if (rule.weekdays) draft.weekdays = [...rule.weekdays];
  return draft as AutomationSchedule;
}

/** The rule the server is sent: the picker's draft without the fields it left empty. */
export function scheduleRuleOf(draft: AutomationSchedule): WorkflowScheduleRule {
  const rule: Record<string, unknown> = { frequency: draft.frequency, time: draft.time, timezone: draft.timezone };
  for (const field of SCHEDULE_FIELDS) {
    const value = draft[field];
    if (value !== undefined && value !== "") rule[field] = value;
  }
  if (draft.weekdays) rule.weekdays = [...draft.weekdays];
  return rule as WorkflowScheduleRule;
}

/**
 * Where an Approval step can send a run back to: every step before it, in
 * the order the run reaches them.
 */
export function approvalRerunChoices(
  graph: WorkflowGraph,
  nodeId: string,
): ReadonlyArray<{ readonly id: string; readonly label: string }> {
  const before = new Set(workflowAncestors(graph, nodeId));
  return workflowTopologicalOrder(graph).flatMap((id) => {
    const node = graph.nodes.find((entry) => entry.id === id);
    return node && before.has(id) ? [{ id, label: node.label }] : [];
  });
}

export type ApprovalStanding = "decide" | "own-run" | "not-approver" | "not-waiting";

/** Where the viewer stands on a manual step (AMO-307): theirs to mark done, someone else's, or not waiting. */
export function checklistStanding(
  run: Pick<WorkflowRun, "startedByEmail" | "steps" | "graph">,
  nodeId: string,
  viewerEmail: string | null | undefined,
): "do" | "not-theirs" | "not-waiting" {
  const step = run.steps.find((entry) => entry.nodeId === nodeId);
  if (step?.status !== "waiting") return "not-waiting";
  const viewer = viewerEmail?.trim().toLowerCase();
  const node = run.graph.nodes.find((entry) => entry.id === nodeId);
  if (!viewer || !node) return "not-theirs";
  return workflowChecklistPeople(node, run.startedByEmail?.trim().toLowerCase() ?? null).includes(viewer) ? "do" : "not-theirs";
}

/**
 * What the viewer may do with a run's Approval step: decide it (they are
 * named and did not start the run), or why not. The server checks again.
 */
export function approvalStanding(
  run: Pick<WorkflowRun, "startedByEmail" | "steps" | "graph">,
  nodeId: string,
  viewerEmail: string | null | undefined,
): ApprovalStanding {
  const step = run.steps.find((entry) => entry.nodeId === nodeId);
  if (step?.status !== "waiting") return "not-waiting";
  const viewer = viewerEmail?.trim().toLowerCase();
  if (!viewer) return "not-approver";
  if (run.startedByEmail?.trim().toLowerCase() === viewer) return "own-run";
  const node = run.graph.nodes.find((entry) => entry.id === nodeId);
  return node && workflowApproverEmails(node).includes(viewer) ? "decide" : "not-approver";
}

export type PartialRunStanding = "waiting" | "answered" | "theirs-only";

export const PARTIAL_RUN_LINE: Readonly<Record<PartialRunStanding, string>> = {
  waiting: "Your step is waiting for you.",
  answered: "You've answered your step. The rest of this run is shown to the person who started it.",
  "theirs-only": "You see only your step of this run. The rest is shown to the person who started it.",
};

export function partialRunStanding(
  run: Pick<WorkflowRun, "startedByEmail" | "steps" | "graph" | "decisions">,
  viewerEmail: string | null | undefined,
): PartialRunStanding {
  if (run.graph.nodes.some((node) => approvalStanding(run, node.id, viewerEmail) === "decide")) return "waiting";
  const viewer = viewerEmail?.trim().toLowerCase();
  return viewer && run.decisions?.some((decision) => decision.byEmail?.trim().toLowerCase() === viewer) ? "answered" : "theirs-only";
}
